import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Input } from "@tubo/ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tubo/ui/components/ui/select";
import { useValueVocabularyList, useForkVocabulary, usePublishVocabulary, getValueVocabularyListQueryKey } from "@tubo/api-models/hooks/value-vocabulary-api.hooks";
import { useSemanticNamespaceList } from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import type { ValueVocabularyListItemVO } from "@tubo/api-models/hooks/value-vocabulary-api.schemas";
import { VocabularyFormDialog } from "@client/components/semantic-platform/vocabulary-form-dialog";
import {
  VocabularyDeleteDialog,
  type VocabularyDeleteTarget,
} from "@client/components/semantic-platform/vocabulary-delete-dialog";
import { NS_CONTEXT_ALL, NS_CONTEXT_GLOBAL, isGlobalNamespace, isPackNamespace } from "@tubo/semantic-concepts";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";
import { ForkDialog } from "@client/components/semantic-platform/fork-dialog";
import { publishStatusVariant } from "@client/components/semantic-platform/publish-status";
import { useUiTranslation } from "@tubo/ui/i18n";

export const Route = createFileRoute("/_app/semantic-platform/vocabularies/")({
  // 领域上下文入 URL（缺省=全局；__all__=全部领域聚合浏览）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: VocabularyListPage,
});

const PAGE_SIZE = 20;

function VocabularyListPage() {
  const { t, i18n } = useUiTranslation("semanticPlatform");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [keyword, setKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VocabularyDeleteTarget | null>(null);
  const [forkTarget, setForkTarget] = useState<ValueVocabularyListItemVO | null>(null);

  // 领域上下文（切换器与列表写同一 URL 参数）；聚合浏览模式下提供领域筛选
  const { ns, nsFilter, setNs } = useNsContext();
  const showNsFilter = ns === NS_CONTEXT_ALL;
  const typeFilterOptions = useMemo(
    () => [
      { value: "all", label: t("vocabularies.list.structureAll") },
      { value: "flat", label: t("vocabularies.list.flat") },
      { value: "hierarchical", label: t("vocabularies.list.hierarchical") },
    ],
    [t],
  );

  const { data, isLoading } = useValueVocabularyList(undefined, { query: {} });
  const { data: namespaces = [] } = useSemanticNamespaceList({ query: {} });
  const nsNameById = useMemo(
    () => new Map(namespaces.map((ns) => [ns.id, ns.name])),
    [namespaces],
  );
  const globalNsIds = useMemo(
    () => new Set(namespaces.filter((nsItem) => isGlobalNamespace(nsItem.code)).map((nsItem) => nsItem.id)),
    [namespaces],
  );
  // 行业包领域 id 集：行只读，编辑触发复制派生
  const packNsIds = useMemo(
    () => new Set(namespaces.filter((nsItem) => isPackNamespace(nsItem.code)).map((nsItem) => nsItem.id)),
    [namespaces],
  );
  const userDomains = useMemo(
    () => namespaces
      .filter((nsItem) => !isPackNamespace(nsItem.code) && !isGlobalNamespace(nsItem.code))
      .map((nsItem) => ({ id: nsItem.id, name: nsItem.name })),
    [namespaces],
  );
  const forkMutation = useForkVocabulary({
    mutation: {
      onSuccess: (detail) => {
        queryClient.invalidateQueries({ queryKey: getValueVocabularyListQueryKey() });
        toast.success(t("vocabularies.list.copiedAs", { name: detail?.name ?? t("navigation.vocabularies") }));
        setForkTarget(null);
      },
      onError: () => toast.error(t("vocabularies.list.copyFailed")),
    },
  });
  const publishMutation = usePublishVocabulary({
    mutation: {
      onSuccess: (detail) => {
        queryClient.invalidateQueries({ queryKey: getValueVocabularyListQueryKey() });
        toast.success(t("vocabularies.list.published", { name: detail?.name ?? t("navigation.vocabularies") }));
      },
      onError: () => toast.error(t("vocabularies.list.publishFailed")),
    },
  });

  const nsFilterOptions = useMemo(
    () => [
      { value: NS_CONTEXT_ALL, label: t("vocabularies.list.allDomains") },
      { value: NS_CONTEXT_GLOBAL, label: t("vocabularies.list.global") },
      ...namespaces
        .filter((nsItem) => !isPackNamespace(nsItem.code) && !isGlobalNamespace(nsItem.code))
        .map((nsItem) => ({ value: nsItem.id, label: nsItem.name })),
    ],
    [namespaces, t],
  );

  useEffect(() => {
    setPage(1);
  }, [keyword, typeFilter, nsFilter]);

  const filteredData = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return (data ?? []).filter((vocabulary) => {
      const matchesKeyword = !normalizedKeyword ||
        [vocabulary.code, vocabulary.name].some((value) => value.toLowerCase().includes(normalizedKeyword));
      const matchesType = typeFilter === "all" ||
        (typeFilter === "hierarchical" ? vocabulary.isHierarchical : !vocabulary.isHierarchical);
      const matchesNs =
        nsFilter === NS_CONTEXT_ALL ||
        (nsFilter === NS_CONTEXT_GLOBAL
          ? !vocabulary.namespaceId || globalNsIds.has(vocabulary.namespaceId)
          : vocabulary.namespaceId === nsFilter);
      return matchesKeyword && matchesType && matchesNs;
    });
  }, [data, globalNsIds, keyword, typeFilter, nsFilter]);

  const columns: DataTableColumn<ValueVocabularyListItemVO>[] = [
    { id: "code", header: t("vocabularies.list.id"), cell: (r) => <span className="font-mono text-sm">{r.code}</span> },
    { id: "name", header: t("vocabularies.list.name"), cell: (r) => (<span className="font-medium">{r.name}{r.origin?.startsWith("pack:") && (<Badge variant="outline" className="ml-1.5 text-[10px]">{t("vocabularies.list.industryDemo")}</Badge>)}</span>) },
    {
      id: "namespace",
      header: t("vocabularies.list.domain"),
      cell: (r) =>
        r.namespaceId ? (
          <Badge variant="outline">{r.namespaceName ?? nsNameById.get(r.namespaceId) ?? r.namespaceId}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">{t("vocabularies.list.global")}</span>
        ),
    },
    {
      id: "status",
      header: t("vocabularies.list.status"),
      cell: (r) => <Badge variant={publishStatusVariant(r.status)}>{r.status === "draft" ? t("vocabularies.list.statusDraft") : r.status === "pendingReview" ? t("vocabularies.list.statusPendingReview") : r.status === "published" ? t("vocabularies.list.statusPublished") : r.status === "deprecated" ? t("vocabularies.list.statusDeprecated") : r.status}</Badge>,
    },
    { id: "itemCount", header: t("vocabularies.list.itemCount"), cell: (r) => r.itemCount },
    {
      id: "isHierarchical",
      header: t("vocabularies.list.type"),
      cell: (r) => (r.isHierarchical ? <Badge variant="outline">{t("vocabularies.list.hierarchical")}</Badge> : <Badge variant="secondary">{t("vocabularies.list.flat")}</Badge>),
    },
    { id: "updatedAt", header: t("vocabularies.list.updatedAt"), cell: (r) => new Date(r.updatedAt).toLocaleString(i18n.resolvedLanguage) },
    {
      id: "actions",
      header: t("vocabularies.list.actions"),
      cell: (r) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("vocabularies.list.edit")}
            title={t("vocabularies.list.edit")}
            onClick={() => (r.namespaceId && packNsIds.has(r.namespaceId) ? setForkTarget(r) : navigate({ to: "/semantic-platform/vocabularies/$id", params: { id: r.id }, search: ns ? { ns } : {} }))}
          >
            <Pencil className="size-3.5" />
          </Button>
          {r.status !== "published" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={publishMutation.isPending}
              title={t("vocabularies.list.publishTitle")}
              onClick={() => publishMutation.mutate({ id: r.id })}
            >
              {t("vocabularies.list.publish")}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("vocabularies.list.delete")}
            title={t("vocabularies.list.delete")}
            className="text-destructive"
            onClick={() => setDeleteTarget({ id: r.id, code: r.code, name: r.name })}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  const total = filteredData.length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(total / pageSize)));
  const pagedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">{t("navigation.vocabularies")}</span>,
    [t],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {showNsFilter && (
          <Select items={nsFilterOptions} value={nsFilter} onValueChange={(value) => setNs(value ?? NS_CONTEXT_ALL)}>
            <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{nsFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select items={typeFilterOptions} value={typeFilter} onValueChange={(value) => setTypeFilter(value ?? "all")}>
          <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{typeFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="w-56 pl-8" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder={t("vocabularies.list.searchPlaceholder")} />
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" /> {t("vocabularies.list.create")}
        </Button>
      </div>
    ),
    [keyword, typeFilter, typeFilterOptions, nsFilter, nsFilterOptions, showNsFilter, setNs, t],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="h-full min-h-0 min-w-0">
      <DataTable
        data={pagedData}
        columns={columns}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        page={currentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={total}
        onPageChange={setPage}
        onRowClick={(row) => navigate({ to: "/semantic-platform/vocabularies/$id", params: { id: row.id }, search: ns ? { ns } : {} })}
        emptyState={
          <div className="py-6 text-center text-sm text-muted-foreground">
            <p>{t("vocabularies.list.emptyTitle")}</p>
            <p className="mt-1 text-xs">{t("vocabularies.list.emptyDescription")}</p>
          </div>
        }
      />

      <VocabularyFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultNamespaceId={nsFilter !== NS_CONTEXT_ALL && nsFilter !== NS_CONTEXT_GLOBAL ? nsFilter : undefined}
      />
      <VocabularyDeleteDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} />

      <ForkDialog
        open={forkTarget !== null}
        onOpenChange={(v) => !v && setForkTarget(null)}
        sourceLabel={forkTarget ? forkTarget.name : undefined}
        domains={userDomains}
        onFork={async (targetNamespaceId) => {
          if (!forkTarget) return;
          await forkMutation.mutateAsync({ id: forkTarget.id, data: { targetNamespaceId } });
          return forkTarget.id;
        }}
      />
    </div>
  );
}
